import {
  BackupTable,
  Edit,
  EmojiEvents,
  StackedBarChart,
  TableChart,
  TrendingUp,
} from '@mui/icons-material'

import { RidgelineIcon } from 'features/charts-v2/components/RidgelineIcon/RidgelineIcon'
import { SankeyIcon } from 'features/charts-v2/components/SankeyIcon/SankeyIcon'
import {
  BREAKDOWNS,
  METRICS,
  type TimeBucketOption,
} from 'features/charts-v2/types'
import {
  type ChartPreset,
  type ChartPresetGroup,
  ChartTypeV2,
  type ChicletConfig,
} from 'features/charts-v2/types/chart-types'
import type { Breakdown } from 'features/charts-v2/types/filter-hash/v2'
import CustomBarChart from 'features/project/features/charts-2/assets/chart-type-icons/child/freeform/CustomBarChart.svg?react'
import CustomBarChartSegmented from 'features/project/features/charts-2/assets/chart-type-icons/child/freeform/CustomBarChartSegmented.svg?react'
import CustomBubbleChart from 'features/project/features/charts-2/assets/chart-type-icons/child/freeform/CustomBubbleChart.svg?react'
import CustomHeatmap from 'features/project/features/charts-2/assets/chart-type-icons/child/freeform/CustomHeatmap.svg?react'
import CustomLineGraph from 'features/project/features/charts-2/assets/chart-type-icons/child/freeform/CustomLineGraph.svg?react'
import CustomMetricsTable from 'features/project/features/charts-2/assets/chart-type-icons/child/freeform/CustomMetricsTable.svg?react'
import CustomPieChart from 'features/project/features/charts-2/assets/chart-type-icons/child/freeform/CustomPieChart.svg?react'
import CustomScatter from 'features/project/features/charts-2/assets/chart-type-icons/child/freeform/CustomScatter.svg?react'
import CustomTreemap from 'features/project/features/charts-2/assets/chart-type-icons/child/freeform/CustomTreemap.svg?react'
import MetricsTable from 'features/project/features/charts-2/assets/chart-type-icons/child/template/MetricsTable.svg?react'
import RatingAndSentimentCorrelation from 'features/project/features/charts-2/assets/chart-type-icons/child/template/RatingAndSentimentCorrelation.svg?react'
import RatingOverTime from 'features/project/features/charts-2/assets/chart-type-icons/child/template/RatingOverTime.svg?react'
import RatingRanking from 'features/project/features/charts-2/assets/chart-type-icons/child/template/RatingRanking.svg?react'
import RatingRankingSegmented from 'features/project/features/charts-2/assets/chart-type-icons/child/template/RatingRankingSegmented.svg?react'
import SentimentOverTime from 'features/project/features/charts-2/assets/chart-type-icons/child/template/SentimentOverTime.svg?react'
import SentimentRanking from 'features/project/features/charts-2/assets/chart-type-icons/child/template/SentimentRanking.svg?react'
import SentimentRankingSegmented from 'features/project/features/charts-2/assets/chart-type-icons/child/template/SentimentRankingSegmented.svg?react'
import ThemeRatingMatrix from 'features/project/features/charts-2/assets/chart-type-icons/child/template/ThemeRatingMatrix.svg?react'
import ThemeSentimentMatrix from 'features/project/features/charts-2/assets/chart-type-icons/child/template/ThemeSentimentMatrix.svg?react'
import VolumeDistributionPie from 'features/project/features/charts-2/assets/chart-type-icons/child/template/VolumeDistributionPie.svg?react'
import VolumeDistributionTreemap from 'features/project/features/charts-2/assets/chart-type-icons/child/template/VolumeDistributionTreemap.svg?react'
import VolumeOverTime from 'features/project/features/charts-2/assets/chart-type-icons/child/template/VolumeOverTime.svg?react'

const NEW_CHICLET: ChicletConfig = {
  icon: null,
  text: 'New',
  tone: 'emerald',
}

// ---------------------------------------------------------------------------
// Chart Preset Constants
// ---------------------------------------------------------------------------

export const RATING_SENTIMENT_CORRELATION_PRESET: ChartPreset = {
  id: 'rating-sentiment-correlation',
  label: 'Rating & Sentiment Correlation',
  description: 'Compare rating and sentiment with volume as bubble size.',
  chartType: ChartTypeV2.BUBBLE,
  metrics: [METRICS.RATING.id, METRICS.SENTIMENT.id, METRICS.SENTENCE_COUNT.id],
  defaultPrimaryBreakdown: null,
  secondaryBreakdown: null,
  timeBucket: null,
  enabled: true,
  icon: <RatingAndSentimentCorrelation />,
  chiclets: [],
}

export const RATING_SENTIMENT_CORRELATION_SCATTER_PRESET: ChartPreset = {
  id: 'rating-sentiment-correlation-scatter',
  label: 'Scatter',
  description: 'Compare rating and sentiment with fixed-width bubble sizes.',
  chartType: ChartTypeV2.SCATTER,
  metrics: [METRICS.RATING.id, METRICS.SENTIMENT.id],
  defaultPrimaryBreakdown: null,
  secondaryBreakdown: null,
  timeBucket: null,
  enabled: true,
  icon: <RatingAndSentimentCorrelation />,
  chiclets: [],
}

export const RATING_RANKING_PRESET: ChartPreset = {
  id: 'rankings-rating',
  label: 'Rating',
  description: 'Rank dimensions by average star rating.',
  chartType: ChartTypeV2.BAR_HORIZONTAL,
  metrics: [METRICS.RATING.id],
  defaultPrimaryBreakdown: BREAKDOWNS.THEME.id,
  secondaryBreakdown: null,
  timeBucket: null,
  enabled: true,
  icon: <RatingRanking />,
  chiclets: [],
}

export const SENTIMENT_RANKING_PRESET: ChartPreset = {
  id: 'rankings-sentiment',
  label: 'Sentiment',
  description: 'Rank dimensions by overall sentiment.',
  chartType: ChartTypeV2.BAR_HORIZONTAL,
  metrics: [METRICS.SENTIMENT.id],
  defaultPrimaryBreakdown: BREAKDOWNS.THEME.id,
  secondaryBreakdown: null,
  timeBucket: null,
  enabled: true,
  icon: <SentimentRanking />,
  chiclets: [],
}

export const VOLUME_OVER_TIME_PRESET: ChartPreset = {
  id: 'time-volume',
  label: 'Volume',
  description: 'Track volume trends over time.',
  chartType: ChartTypeV2.LINE,
  metrics: [METRICS.RECORD_COUNT.id],
  defaultPrimaryBreakdown: BREAKDOWNS.THEME.id,
  secondaryBreakdown: null,
  timeBucket: 'month',
  enabled: true,
  icon: <VolumeOverTime />,
  chiclets: [],
}

export const RATING_OVER_TIME_PRESET: ChartPreset = {
  id: 'time-rating',
  label: 'Rating',
  description: 'Track star rating changes over time.',
  chartType: ChartTypeV2.LINE,
  metrics: [METRICS.RATING.id],
  defaultPrimaryBreakdown: BREAKDOWNS.THEME.id,
  secondaryBreakdown: null,
  timeBucket: 'month',
  enabled: true,
  icon: <RatingOverTime />,
  chiclets: [],
}

export const SENTIMENT_OVER_TIME_PRESET: ChartPreset = {
  id: 'time-sentiment',
  label: 'Sentiment',
  description: 'Track sentiment changes over time.',
  chartType: ChartTypeV2.LINE,
  metrics: [METRICS.SENTIMENT.id],
  defaultPrimaryBreakdown: BREAKDOWNS.THEME.id,
  secondaryBreakdown: null,
  timeBucket: 'month',
  enabled: true,
  icon: <SentimentOverTime />,
  chiclets: [],
}

export const RATING_VOLUME_OVER_TIME_PRESET: ChartPreset = {
  id: 'time-rating-volume',
  label: 'Rating & Volume',
  description: 'Track star rating and volume over time.',
  chartType: ChartTypeV2.LINE,
  metrics: [METRICS.RATING.id, METRICS.RECORD_COUNT.id],
  defaultPrimaryBreakdown: BREAKDOWNS.THEME.id,
  secondaryBreakdown: null,
  timeBucket: 'month',
  enabled: true,
  icon: <RatingOverTime />,
  chiclets: [],
}

export const SENTIMENT_VOLUME_OVER_TIME_PRESET: ChartPreset = {
  id: 'time-sentiment-volume',
  label: 'Sentiment & Volume',
  description: 'Track sentiment and volume over time.',
  chartType: ChartTypeV2.LINE,
  metrics: [METRICS.SENTIMENT.id, METRICS.SENTENCE_COUNT.id],
  defaultPrimaryBreakdown: BREAKDOWNS.THEME.id,
  secondaryBreakdown: null,
  timeBucket: 'month',
  enabled: true,
  icon: <SentimentOverTime />,
  chiclets: [],
}

export const RATING_COMPOSITION_PRESET: ChartPreset = {
  id: 'composition-rating',
  label: 'Rating Composition',
  description: 'Compare rating composition across dimensions.',
  chartType: ChartTypeV2.BAR_HORIZONTAL_STACKED,
  metrics: [METRICS.RECORD_COUNT.id],
  defaultPrimaryBreakdown: BREAKDOWNS.THEME.id,
  secondaryBreakdown: BREAKDOWNS.RATING.id,
  timeBucket: null,
  enabled: true,
  icon: <RatingRankingSegmented />,
  chiclets: [],
}

export const SENTIMENT_COMPOSITION_PRESET: ChartPreset = {
  id: 'composition-sentiment',
  label: 'Sentiment Composition',
  description: 'Compare sentiment composition across dimensions.',
  chartType: ChartTypeV2.BAR_HORIZONTAL_STACKED,
  metrics: [METRICS.SENTENCE_COUNT.id],
  defaultPrimaryBreakdown: BREAKDOWNS.THEME.id,
  secondaryBreakdown: BREAKDOWNS.SENTIMENT.id,
  timeBucket: null,
  enabled: true,
  icon: <SentimentRankingSegmented />,
  chiclets: [],
}

export const THEME_BRAND_RATING_PRESET: ChartPreset = {
  id: 'theme-brand-rating',
  label: 'Theme Brand Rating',
  labelTemplate: '{primary} Brand Rating',
  description: 'Compare ratings across theme and brand.',
  descriptionTemplate: 'Compare ratings across {primary} and brand.',
  chartType: ChartTypeV2.HEATMAP,
  metrics: [METRICS.RATING.id, METRICS.RECORD_COUNT.id],
  defaultPrimaryBreakdown: BREAKDOWNS.THEME.id,
  secondaryBreakdown: BREAKDOWNS.BRAND.id,
  timeBucket: null,
  enabled: true,
  icon: <ThemeRatingMatrix />,
  chiclets: [],
}

export const THEME_BRAND_SENTIMENT_PRESET: ChartPreset = {
  id: 'theme-brand-sentiment',
  label: 'Theme Brand Sentiment',
  labelTemplate: '{primary} Brand Sentiment',
  description: 'Compare sentiment across theme and brand.',
  descriptionTemplate: 'Compare sentiment across {primary} and brand.',
  chartType: ChartTypeV2.HEATMAP,
  metrics: [METRICS.SENTIMENT.id, METRICS.SENTENCE_COUNT.id],
  defaultPrimaryBreakdown: BREAKDOWNS.THEME.id,
  secondaryBreakdown: BREAKDOWNS.BRAND.id,
  timeBucket: null,
  enabled: true,
  icon: <ThemeSentimentMatrix />,
  chiclets: [],
}

export const TREEMAP_PRESET: ChartPreset = {
  id: 'treemap',
  label: 'Treemap',
  description: 'View theme distribution as a treemap.',
  chartType: ChartTypeV2.TREEMAP,
  metrics: [
    METRICS.RECORD_COUNT.id,
    METRICS.SENTENCE_COUNT.id,
    METRICS.RATING.id,
    METRICS.SENTIMENT.id,
  ],
  defaultPrimaryBreakdown: null,
  secondaryBreakdown: null,
  timeBucket: null,
  enabled: true,
  icon: <VolumeDistributionTreemap />,
  chiclets: [],
}

export const PIE_CHART_PRESET: ChartPreset = {
  id: 'pie',
  label: 'Pie Chart',
  description: 'View theme distribution as a pie chart.',
  chartType: ChartTypeV2.PIE,
  metrics: [METRICS.RECORD_COUNT.id],
  defaultPrimaryBreakdown: null,
  secondaryBreakdown: null,
  timeBucket: null,
  enabled: true,
  icon: <VolumeDistributionPie />,
  chiclets: [],
}

export const SENTIMENT_DISTRIBUTION_PRESET: ChartPreset = {
  id: 'distribution-sentiment',
  label: 'Sentiment Distribution',
  description: 'See the full range of sentiment scores for each theme.',
  chartType: ChartTypeV2.RIDGELINE,
  metrics: [METRICS.SENTIMENT.id],
  defaultPrimaryBreakdown: BREAKDOWNS.THEME.id,
  secondaryBreakdown: null,
  timeBucket: null,
  enabled: true,
  icon: <RidgelineIcon />,
  chiclets: [NEW_CHICLET],
}

export const RATING_DISTRIBUTION_PRESET: ChartPreset = {
  id: 'distribution-rating',
  label: 'Rating Distribution',
  description: 'See the full range of star ratings for each theme.',
  chartType: ChartTypeV2.RIDGELINE,
  metrics: [METRICS.RATING.id],
  defaultPrimaryBreakdown: BREAKDOWNS.THEME.id,
  secondaryBreakdown: null,
  timeBucket: null,
  enabled: true,
  icon: <RidgelineIcon />,
  chiclets: [NEW_CHICLET],
}

export const TOPIC_HIERARCHY_PRESET: ChartPreset = {
  id: 'flow-topic-hierarchy',
  label: 'Topic Hierarchy',
  description: 'See how themes break down into their subthemes.',
  chartType: ChartTypeV2.SANKEY,
  metrics: [METRICS.SENTENCE_COUNT.id],
  defaultPrimaryBreakdown: BREAKDOWNS.THEME.id,
  secondaryBreakdown: null,
  breakdowns: [{ kind: 'standard', id: 'theme', grouped: true }],
  timeBucket: null,
  enabled: true,
  icon: <SankeyIcon />,
  chiclets: [NEW_CHICLET],
}

export const BRAND_FLOW_PRESET: ChartPreset = {
  id: 'flow-brand',
  label: 'Brand Sentiment Flow',
  description: 'See how sentiment splits across themes for each brand.',
  chartType: ChartTypeV2.SANKEY,
  metrics: [METRICS.SENTENCE_COUNT.id],
  defaultPrimaryBreakdown: BREAKDOWNS.BRAND.id,
  secondaryBreakdown: BREAKDOWNS.THEME.id,
  breakdowns: [
    { kind: 'standard', id: 'brand', grouped: false },
    { kind: 'standard', id: 'theme', grouped: true },
  ],
  timeBucket: null,
  enabled: true,
  icon: <SankeyIcon />,
  chiclets: [NEW_CHICLET],
  flowColorMode: 'sentiment',
}

export const SUMMARY_METRICS_PRESET: ChartPreset = {
  id: 'summary-metrics',
  label: 'Summary Metrics',
  description: 'View detailed summary metrics in a table.',
  chartType: ChartTypeV2.TABLE,
  metrics: [METRICS.RECORD_COUNT.id, METRICS.SENTIMENT.id, METRICS.RATING.id],
  defaultPrimaryBreakdown: BREAKDOWNS.THEME.id,
  secondaryBreakdown: null,
  timeBucket: null,
  enabled: true,
  icon: <MetricsTable />,
  chiclets: [],
}

export const FREEFORM_BAR_PRESET: ChartPreset = {
  id: 'freeform-bar',
  label: 'Bar chart',
  description: 'Compare values across categories.',
  chartType: ChartTypeV2.BAR_HORIZONTAL,
  metrics: [METRICS.SENTIMENT.id],
  defaultPrimaryBreakdown: BREAKDOWNS.THEME.id,
  secondaryBreakdown: null,
  timeBucket: null,
  enabled: true,
  icon: <CustomBarChart />,
  chiclets: [],
}

export const FREEFORM_BUBBLE_PRESET: ChartPreset = {
  id: 'freeform-bubble',
  label: 'Bubble chart',
  description: 'Compare three metrics at once.',
  chartType: ChartTypeV2.BUBBLE,
  metrics: [METRICS.SENTIMENT.id, METRICS.RATING.id, METRICS.SENTENCE_COUNT.id],
  defaultPrimaryBreakdown: BREAKDOWNS.THEME.id,
  secondaryBreakdown: null,
  timeBucket: null,
  enabled: true,
  icon: <CustomBubbleChart />,
  chiclets: [],
}

export const FREEFORM_LINE_PRESET: ChartPreset = {
  id: 'freeform-line',
  label: 'Line chart',
  description: 'Track changes over time.',
  chartType: ChartTypeV2.LINE,
  metrics: [METRICS.SENTENCE_COUNT.id],
  defaultPrimaryBreakdown: BREAKDOWNS.THEME.id,
  secondaryBreakdown: null,
  timeBucket: 'month',
  enabled: true,
  icon: <CustomLineGraph />,
  chiclets: [],
}

export const FREEFORM_PIE_PRESET: ChartPreset = {
  id: 'freeform-pie',
  label: 'Pie chart',
  description: 'Show proportions of a whole.',
  chartType: ChartTypeV2.PIE,
  metrics: [METRICS.SENTENCE_COUNT.id],
  defaultPrimaryBreakdown: BREAKDOWNS.THEME.id,
  secondaryBreakdown: null,
  timeBucket: null,
  enabled: true,
  icon: <CustomPieChart />,
  chiclets: [],
}

export const FREEFORM_SCATTER_PRESET: ChartPreset = {
  id: 'freeform-scatter',
  label: 'Scatter chart',
  description: 'Explore relationships between metrics.',
  chartType: ChartTypeV2.SCATTER,
  metrics: [METRICS.RATING.id, METRICS.SENTIMENT.id],
  defaultPrimaryBreakdown: BREAKDOWNS.THEME.id,
  secondaryBreakdown: null,
  timeBucket: null,
  enabled: true,
  icon: <CustomScatter />,
  chiclets: [],
}

export const FREEFORM_RIDGELINE_PRESET: ChartPreset = {
  id: 'freeform-ridgeline',
  label: 'Ridgeline chart',
  description: 'See the full range of a score across groups.',
  chartType: ChartTypeV2.RIDGELINE,
  metrics: [METRICS.SENTIMENT.id],
  defaultPrimaryBreakdown: BREAKDOWNS.THEME.id,
  secondaryBreakdown: null,
  timeBucket: null,
  enabled: true,
  icon: <RidgelineIcon />,
  chiclets: [NEW_CHICLET],
}

export const FREEFORM_SANKEY_PRESET: ChartPreset = {
  id: 'freeform-sankey',
  label: 'Sankey chart',
  description: 'See how volume flows between two or three groups.',
  chartType: ChartTypeV2.SANKEY,
  metrics: [METRICS.SENTENCE_COUNT.id],
  defaultPrimaryBreakdown: BREAKDOWNS.BRAND.id,
  secondaryBreakdown: BREAKDOWNS.THEME.id,
  breakdowns: [
    { kind: 'standard', id: 'brand', grouped: false },
    { kind: 'standard', id: 'theme', grouped: false },
  ],
  timeBucket: null,
  enabled: true,
  icon: <SankeyIcon />,
  chiclets: [NEW_CHICLET],
}

export const FREEFORM_STACKED_BAR_PRESET: ChartPreset = {
  id: 'freeform-stacked-bar',
  label: 'Stacked bar chart',
  description: 'Compare composition across categories.',
  chartType: ChartTypeV2.BAR_HORIZONTAL_STACKED,
  metrics: [METRICS.SENTENCE_COUNT.id],
  defaultPrimaryBreakdown: BREAKDOWNS.THEME.id,
  secondaryBreakdown: BREAKDOWNS.SENTIMENT.id,
  timeBucket: null,
  enabled: true,
  icon: <CustomBarChartSegmented />,
  chiclets: [],
}

export const FREEFORM_TABLE_PRESET: ChartPreset = {
  id: 'freeform-table',
  label: 'Table',
  description: 'View detailed data in rows and columns.',
  chartType: ChartTypeV2.TABLE,
  metrics: [METRICS.RECORD_COUNT.id, METRICS.SENTIMENT.id, METRICS.RATING.id],
  defaultPrimaryBreakdown: BREAKDOWNS.THEME.id,
  secondaryBreakdown: null,
  timeBucket: null,
  enabled: true,
  icon: <CustomMetricsTable />,
  chiclets: [],
}

export const FREEFORM_HEATMAP_PRESET: ChartPreset = {
  id: 'freeform-heatmap',
  label: 'Heatmap',
  description: 'Compare two dimensions in a matrix format.',
  chartType: ChartTypeV2.HEATMAP,
  metrics: [METRICS.SENTIMENT.id, METRICS.SENTENCE_COUNT.id],
  defaultPrimaryBreakdown: BREAKDOWNS.THEME.id,
  secondaryBreakdown: BREAKDOWNS.BRAND.id,
  timeBucket: null,
  enabled: true,
  icon: <CustomHeatmap />,
  chiclets: [],
}

export const FREEFORM_TREEMAP_PRESET: ChartPreset = {
  id: 'freeform-treemap',
  label: 'Treemap',
  description: 'View hierarchical data as nested rectangles.',
  chartType: ChartTypeV2.TREEMAP,
  metrics: [METRICS.RECORD_COUNT.id, METRICS.SENTIMENT.id, METRICS.RATING.id],
  defaultPrimaryBreakdown: null,
  secondaryBreakdown: null,
  timeBucket: null,
  enabled: true,
  icon: <CustomTreemap />,
  chiclets: [],
}

/**
 * All chart preset groups organized by user intent
 */
const FREEFORM_PRESETS: ChartPreset[] = [
  FREEFORM_BAR_PRESET,
  FREEFORM_BUBBLE_PRESET,
  FREEFORM_LINE_PRESET,
  FREEFORM_PIE_PRESET,
  FREEFORM_RIDGELINE_PRESET,
  FREEFORM_SANKEY_PRESET,
  FREEFORM_SCATTER_PRESET,
  FREEFORM_STACKED_BAR_PRESET,
  FREEFORM_TABLE_PRESET,
  FREEFORM_HEATMAP_PRESET,
  FREEFORM_TREEMAP_PRESET,
]

export const CHART_PRESET_GROUPS: ChartPresetGroup[] = [
  {
    key: 'rankings',
    label: 'Rankings',
    icon: <EmojiEvents style={{ fontSize: '16px' }} />,
    presets: [
      RATING_SENTIMENT_CORRELATION_PRESET,
      RATING_RANKING_PRESET,
      SENTIMENT_RANKING_PRESET,
      RATING_SENTIMENT_CORRELATION_SCATTER_PRESET,
    ],
  },
  {
    key: 'time',
    label: 'Time',
    icon: <TrendingUp style={{ fontSize: '16px' }} />,
    presets: [
      VOLUME_OVER_TIME_PRESET,
      RATING_OVER_TIME_PRESET,
      SENTIMENT_OVER_TIME_PRESET,
      RATING_VOLUME_OVER_TIME_PRESET,
      SENTIMENT_VOLUME_OVER_TIME_PRESET,
    ],
  },
  {
    key: 'composition',
    label: 'Composition',
    icon: <StackedBarChart style={{ fontSize: '16px', rotate: '90deg' }} />,
    presets: [
      RATING_COMPOSITION_PRESET,
      SENTIMENT_COMPOSITION_PRESET,
      THEME_BRAND_RATING_PRESET,
      THEME_BRAND_SENTIMENT_PRESET,
    ],
  },
  {
    key: 'distribution',
    label: 'Distribution',
    icon: <BackupTable style={{ fontSize: '16px' }} />,
    presets: [
      TREEMAP_PRESET,
      PIE_CHART_PRESET,
      SENTIMENT_DISTRIBUTION_PRESET,
      RATING_DISTRIBUTION_PRESET,
      TOPIC_HIERARCHY_PRESET,
      BRAND_FLOW_PRESET,
    ],
  },
  {
    key: 'metrics',
    label: 'Metrics',
    icon: <TableChart style={{ fontSize: '16px' }} />,
    presets: [SUMMARY_METRICS_PRESET],
  },
  {
    key: 'freeform',
    label: 'Freeform',
    icon: <Edit style={{ fontSize: '16px' }} />,
    presets: FREEFORM_PRESETS,
  },
]

/**
 * Get a preset for a specific chart type
 * Searches through all groups to find a preset matching the chart type
 */
export const getChartTypePreset = (
  chartType: ChartTypeV2,
): ChartPreset | undefined => {
  for (const group of CHART_PRESET_GROUPS) {
    const preset = group.presets.find((p) => p.chartType === chartType)
    if (preset) {
      return preset
    }
  }
  return undefined
}

/**
 * The blue monochrome (freeform) icon for a chart type. `getChartTypePreset`
 * can't serve this: template groups come first and carry colorful icons. Falls
 * back to the freeform bar icon for types with no freeform preset.
 */
export const getFreeformChartTypeIcon = (
  chartType: ChartTypeV2,
): React.ReactNode =>
  (
    FREEFORM_PRESETS.find((p) => p.chartType === chartType) ??
    FREEFORM_BAR_PRESET
  ).icon

export const getPresetById = (id: string): ChartPreset | undefined => {
  for (const group of CHART_PRESET_GROUPS) {
    const preset = group.presets.find((p) => p.id === id)
    if (preset) {
      return preset
    }
  }
  return undefined
}

/**
 * Resolve the display title for a preset given its current primary breakdown.
 *
 * For presets that carry a `labelTemplate` (e.g. '{primary} Brand Rating'), the
 * `{primary}` token is replaced with the primary breakdown's human-readable
 * label. Falls back to the static `preset.label` when no template is present or
 * when the breakdown cannot be resolved.
 */
export function resolvePresetTitle(
  preset: ChartPreset,
  primaryBreakdown: Breakdown | null | undefined,
): string {
  if (!preset.labelTemplate) return preset.label
  const primaryLabel = ((): string | null => {
    if (!primaryBreakdown) return null
    if (primaryBreakdown.kind === 'custom') {
      return primaryBreakdown.alias || primaryBreakdown.field
    }
    const config = Object.values(BREAKDOWNS).find(
      (b) => b.id === primaryBreakdown.id,
    )
    return config?.label ?? primaryBreakdown.id
  })()
  return primaryLabel
    ? preset.labelTemplate.replace('{primary}', primaryLabel)
    : preset.label
}

/**
 * Resolve the time bucket to apply when switching a panel to `preset`.
 *
 * Every "over time" preset ships `timeBucket: 'month'`, so applying the preset
 * default unconditionally reverted a user-selected bucket (e.g. `day`) back to
 * `month` every time they switched between line-chart presets (PROD-4517). A
 * user-selected bucket must win: keep the current bucket when the incoming
 * preset is itself time-bucketed, adopt the preset default only when the panel
 * had no bucket, and clear it for a non-time preset (`preset.timeBucket` null).
 */
export function resolvePresetTimeBucket(
  currentTimeBucket: TimeBucketOption | null,
  preset: ChartPreset,
): TimeBucketOption | null {
  if (preset.timeBucket === null) return null
  return currentTimeBucket ?? preset.timeBucket
}

/**
 * Resolve the card description for a preset given its current primary breakdown.
 *
 * For presets that carry a `descriptionTemplate`, the `{primary}` token is
 * replaced with the **lowercase** form of the primary breakdown's label (labels
 * are Title Case, e.g. "Theme" → "theme"). Falls back to the static
 * `preset.description` when no template is present or the breakdown is absent.
 */
export function resolvePresetDescription(
  preset: ChartPreset,
  primaryBreakdown: Breakdown | null | undefined,
): string {
  if (!preset.descriptionTemplate) return preset.description
  const primaryLabel = ((): string | null => {
    if (!primaryBreakdown) return null
    if (primaryBreakdown.kind === 'custom') {
      return (primaryBreakdown.alias || primaryBreakdown.field).toLowerCase()
    }
    const config = Object.values(BREAKDOWNS).find(
      (b) => b.id === primaryBreakdown.id,
    )
    return (config?.label ?? primaryBreakdown.id).toLowerCase()
  })()
  return primaryLabel
    ? preset.descriptionTemplate.replace('{primary}', primaryLabel)
    : preset.description
}
